// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { LMFormRequestService } from '@app/components/form';
import { LMUrlUtilService } from '@app/services/utils/lm-url-util.service';
import {
  SohoBusyIndicatorDirective,
  SohoDataGridComponent,
} from 'ids-enterprise-ng';
import { throwError } from 'rxjs';
import { catchError, finalize, first } from 'rxjs/operators';
// import { LMCreateReportSearchListDialogService } from '@report/index';
import { AppConstants, FormRequestType } from '@app/utils/app.constants';
import { LMErrorUtilService } from '@app/services';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'lm-create-report-search-list-dialog',
  standalone: false,
  templateUrl: './lm-create-report-search-list-dialog.component.html',
  styleUrls: ['./lm-create-report-search-list-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  })
export class LMCreateReportSearchListDialogComponent implements OnInit {
  @ViewChild(SohoDataGridComponent, { static: true }) datagrid: SohoDataGridComponent;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;

  @Input() reportType;
  @Input() url;
  @Input() businessClass;

  EMPTY_DATA: SohoEmptyMessageOptions = {
    title: Soho.Locale.translate('NoDataAvailable'),
    icon: 'icon-empty-no-data-new',
    color: 'graphite'
  };

  businessClasses = [];
  gridOptions = {};
  emptyMessage: SohoEmptyMessageOptions = this.EMPTY_DATA;
  dataDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-create-report-list-data' } ];

  dataTypes = [
    { value: '', text: '' },
    { value: 'TRANSACTIONAL', text: Soho.Locale.translate('Transactional') },
    { value: 'COLUMNAR', text: Soho.Locale.translate('Columnar') },
    { value: 'AUDIT', text: Soho.Locale.translate('Audit') },
    { value: 'CUBE', text: Soho.Locale.translate('Cube') },
  ];

  comparators = [
    {
      value: 'contains',
      text: Soho.Locale.translate('Contains'),
      icon: 'filter-contains',
    },
    {
      value: 'startsWith',
      text: Soho.Locale.translate('StartsWith'),
      icon: 'filter-start-with',
    },
    {
      value: 'endsWith',
      text: Soho.Locale.translate('EndsWith'),
      icon: 'filter-end-with',
    },
    {
      value: 'equals',
      text: Soho.Locale.translate('Equals'),
      icon: 'filter-equals',
    },
  ];

  searchOptions = {
    dataType: '',
    title: {
      value: '',
      filterType: 'contains',
    },
    list: {
      value: '',
      filterType: 'contains',
    },
    owner: {
      value: '',
      filterType: 'contains',
    },
  };

  LMListCellTooltip = (
    row: number,
    cell: number,
    value: any,
    columnDef: ListGridColumn,
    dataView: LMDataView,
    dataGrid: SohoDataGridStatic
  ): string => {
    if (columnDef.field === 'filter') {
      return value;
    }
  };

  LMFieldFormatter = (
    row: number,
    cell: number,
    value: any,
    columnDef: ListGridColumn,
    dataView: LMDataView,
    dataGrid: SohoDataGridStatic
  ): string =>
    value
      ? `<div style="text-align:center">
      <svg class="icon datagrid-alert-icon icon-success" focusable="false" aria-hidden="true" role="presentation">
      <use xlink:href="#icon-success"></use></svg></div>`
      : '';

  constructor(
    private lmFormRequestService: LMFormRequestService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmErrorUtilService: LMErrorUtilService
  ) {}

  ngOnInit() {
    this.getListSet();
    this.gridOptions = this.getGridOptions();
  }

  getGridOptions() {
    const columns: SohoDataGridColumn[] = [
      {
        id: 'field',
        name: Soho.Locale.translate('ListName'),
        sortable: false,
        field: 'name',
        textOverflow: 'ellipsis',
      },
      {
        id: 'field',
        name: Soho.Locale.translate(
          this.reportType === 'ReportCopiedFromList'
            ? 'Title'
            : 'ListPersonalizationReportNameLabel'
        ),
        sortable: false,
        field: 'title',
        textOverflow: 'ellipsis',
      },
      {
        id: 'field',
        name: Soho.Locale.translate('Filtered'),
        sortable: false,
        field: 'filter',
        textOverflow: 'ellipsis',
        align: 'center',
        tooltip: this.LMListCellTooltip,
        formatter: this.LMFieldFormatter,
      },
    ];

    const viewColumn = <SohoDataGridColumn>{
      id: 'field',
      name: Soho.Locale.translate('View'),
      sortable: false,
      field: 'view',
      align: 'center',
      textOverflow: 'ellipsis',
    };

    const ownerColumn = <SohoDataGridColumn>{
      id: 'field',
      name: Soho.Locale.translate('Owner'),
      sortable: false,
      field: 'owner',
      align: 'center',
      textOverflow: 'ellipsis',
    };

    if (this.reportType === 'ReportCopiedFromReport') {
      columns.push(ownerColumn, viewColumn);
    } else {
      columns.push(viewColumn);
    }

    return <SohoDataGridOptions>{
      columns,
      rowHeight: 'extra-small',
      isList: true,
      clickToSelect: true,
      selectable: 'single',
      enableTooltips: true,
      disableRowDeselection: true
    };
  }

  getListSet() {
    this.busyIndicator.activated = true;
    const reportUrl = this.lmUrlUtilService.getReportUrl(this.businessClass);
    const requestType = this.reportType === 'ReportCopiedFromList'
      ? FormRequestType.ListNamesForCreateReport
      : FormRequestType.ReportNamesForCreateReport;

    this.lmFormRequestService
      .getListDetails(reportUrl, requestType, this.businessClass, this.searchOptions)
      .pipe(
        first(),
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.error != null && 
            (error.error?.status === AppConstants.STATUS_SYSTEM_ERROR || error.error?.statusText === AppConstants.STATUS_SYSTEM_ERROR)) {
            this.lmErrorUtilService.handleViewException(error.error);
          }
          return throwError(() => error);
        }),
        finalize(() => this.busyIndicator.activated = false)
      )
      .subscribe((data: IReport[]) => {
        this.datagrid.dataset = data.map((list) => ({
          ...list,
          view: Soho.Locale.translate(list.view),
        }));
        if (data.length > 0) {
          this.datagrid.selectRows(0);
        }
      });
  }

  onFilterCriteriaChange(e: KeyboardEvent) {
    e.preventDefault();
    this.getListSet();
  }

  onListComparatorChange(event: SohoFieldFilteredEvent) {
    this.searchOptions.list.filterType = event?.filterOption?.value;
    if (this.searchOptions.list.value !== '') {
      this.getListSet();
    }
  }

  onTitleComparatorChange(event: SohoFieldFilteredEvent) {
    this.searchOptions.title.filterType = event?.filterOption?.value;
    if (this.searchOptions.title.value !== '') {
      this.getListSet();
    }
  }

  onOwnerComparatorChange(event: SohoFieldFilteredEvent) {
    this.searchOptions.owner.filterType = event?.filterOption?.value;
    if (this.searchOptions.owner.value !== '') {
      this.getListSet();
    }
  }

  onRowSelected(event: SohoDataGridSelectedEvent) {
    if (event.type === 'deselectall') {
      return;
    }
  }
}
